import { Breadcrumb, Form, Modal, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { routes } from "config/routes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronRight, FaCog, FaEye } from "react-icons/fa";
import NewTemplateSettingsForm from "views/Settings/Customization/components/Forms/NewTemplateSettingsForm";
import Template1 from "../../Settings/Customization/components/Template1";
import Template2 from "../../Settings/Customization/components/Template2";
import Template3 from "../../Settings/Customization/components/Template3";
import Template4 from "../../Settings/Customization/components/Template4";
import useCostumizationApi from "../../Settings/Customization/hooks/useCostumizationApi";
import useTemplateApi from "../../Settings/Customization/hooks/useTemplateApi";
import useInvoiceTemplateModal from "../hooks/useInvoiceTemplateModal";
import { useParams } from "react-router-dom";

export default function TemplateChangedModal() {
  const [form] = useForm();
  const { t } = useTranslation();
  const { companyId } = useParams();

  const { isTemplateModal, closeTemplateModal } = useInvoiceTemplateModal();

  const { templateFonts, listTemplateFonts, getCompanySaleDocumentDesign } = useCostumizationApi(
    undefined,
    form,
  );
  const { getCompanyWithAddress, getCompanyBankAccount, companyData, companyBankAccount } =
    useTemplateApi();

  const currentTemplateId = Form.useWatch(["template"], form);

  const newFontsOptions = templateFonts?.map(item => ({
    label: <span className={item?.label.replace(/\s+/g, "_")?.toLowerCase()}>{item?.label}</span>,
    value: item?.value,
  }));

  useEffect(() => {
    if (isTemplateModal) {
      if (companyId) {
        getCompanySaleDocumentDesign({
          variables: { companyId: companyId },
        });

        getCompanyWithAddress({
          variables: {
            id: companyId,
            company_In: [companyId],
            limit: 2,
          },
        });
        getCompanyBankAccount({ variables: { companyId: companyId, limit: 2 } });
      }
      listTemplateFonts();
    }
  }, [isTemplateModal, companyId]);

  const renderTemplate = () => {
    const safeCompanyData = companyData as {
      company: GetCompanyWithAddressCompany;
      address: AddressResult;
    };
    const safeBankAccount = companyBankAccount as BankAccountResult;

    switch (currentTemplateId) {
      case "1":
        return (
          <Template1
            form={form}
            newFontsOptions={newFontsOptions}
            companyData={safeCompanyData}
            companyBankAccount={safeBankAccount}
          />
        );
      case "2":
        return (
          <Template2
            form={form}
            newFontsOptions={newFontsOptions}
            companyData={safeCompanyData}
            companyBankAccount={safeBankAccount}
          />
        );
      case "3":
        return (
          <Template3
            form={form}
            newFontsOptions={newFontsOptions}
            companyData={safeCompanyData}
            companyBankAccount={safeBankAccount}
          />
        );
      case "4":
        return (
          <Template4
            form={form}
            newFontsOptions={newFontsOptions}
            companyData={safeCompanyData}
            companyBankAccount={safeBankAccount}
          />
        );
      default:
        return (
          <Template2
            form={form}
            newFontsOptions={newFontsOptions}
            companyData={safeCompanyData}
            companyBankAccount={safeBankAccount}
          />
        );
    }
  };

  return (
    <Modal
      open={isTemplateModal}
      onCancel={closeTemplateModal}
      onOk={closeTemplateModal}
      width={900}
      centered
      footer={null}
      bodyStyle={{ padding: 0 }}
      className="template-changed-modal"
    >
      <div style={{ display: "none" }}>
        <Form id="customTemplate" layout="vertical" autoComplete="off" form={form}>
          <NewTemplateSettingsForm newFontsOptions={newFontsOptions} form={form} />
        </Form>
      </div>

      <div className="template-modal-content">
        <div className="template-modal-header">
          <Typography.Text className="template-announcement">
            🎉 {t("NeoFact has improved the layout of its invoices! Your selected")}
            <span className="template-highlight">
              {t("Template")} {currentTemplateId}
            </span>
            {t("now features a modern, professional design.")}
          </Typography.Text>
          <Typography.Text className="template-subtitle">
            {t("Preview your new template below and see how it enhances your business documents.")}
          </Typography.Text>
        </div>

        <div className="template-navigation-guide">
          <div className="navigation-label">
            <FaCog className="navigation-icon" />
            {t("To customize your template, navigate to")}:
          </div>
          <Breadcrumb className="template-breadcrumb" separator={<FaChevronRight />}>
            <Breadcrumb.Item>
              <span className="breadcrumb-item">{t("Settings")}</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span
                className="breadcrumb-item breadcrumb-link"
                onClick={() => routes.navigate(`/company/${companyId}/settings/customization`)}
                onKeyDown={e => {
                  if (e.key === "Enter")
                    routes.navigate(`/company/${companyId}/settings/customization`);
                }}
                role="link"
                tabIndex={0}
              >
                {t("Customization")}
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span
                className="breadcrumb-item breadcrumb-link"
                onClick={() =>
                  routes.navigate(`/company/${companyId}/settings/customization/template`)
                }
                onKeyDown={e => {
                  if (e.key === "Enter")
                    routes.navigate(`/company/${companyId}/settings/customization/template`);
                }}
                role="link"
                tabIndex={0}
              >
                {t("Template")}
              </span>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div className="template-preview-container">
        <div className="preview-header">
          <div className="preview-title">
            <FaEye className="preview-icon" />
            {t("Template Preview")}
          </div>
          <Typography.Text className="preview-subtitle">
            {t("This is how your invoices will look with the new template")}
          </Typography.Text>
        </div>

        <div className="template-preview-wrapper">
          <div className="template-preview-frame">{renderTemplate()}</div>
        </div>
      </div>
    </Modal>
  );
}
